import React from 'react';

type SharePreviewStatus = 'idle' | 'loading' | 'ready' | 'error';

type StickerPublishSharePanelProps = {
  stickerId: number | null;
  sharePreviewStatus: SharePreviewStatus;
  shareCheckCount: number;
  shareNextCheckIn: number | null;
  shareResourceUrl: string | null;
  sharePreviewUrl: string | null;
  shareComposerBusy: boolean;
  isMiniApp: boolean;
  hasTimedOut?: boolean;
  onShareX: () => void;
  onShareFarcaster: () => void;
  onClose?: () => void;
};

export default function StickerPublishSharePanel({
  stickerId,
  sharePreviewStatus,
  shareCheckCount,
  shareNextCheckIn,
  shareResourceUrl,
  sharePreviewUrl,
  shareComposerBusy,
  isMiniApp,
  hasTimedOut = false,
  onShareX,
  onShareFarcaster,
  onClose,
}: StickerPublishSharePanelProps) {
  if (!stickerId) {
    return null;
  }

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-left">
      {(sharePreviewStatus === 'loading' || sharePreviewStatus === 'error') && (
        <p className="text-sm font-semibold text-slate-800">
          Checking for your share preview...
        </p>
      )}
      {sharePreviewStatus === 'error' && (
        <p className="text-sm text-slate-600 mt-2">
          {hasTimedOut
            ? 'The preview image is taking longer than expected to generate. Please check again later.'
            : 'A preview image optimized for social media is being generated.'}
        </p>
      )}
      {hasTimedOut && onClose && (
        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
        >
          Close
        </button>
      )}
      {(sharePreviewStatus === 'loading' || sharePreviewStatus === 'error') &&
        shareResourceUrl &&
        !hasTimedOut && (
        <div className="mt-2 text-xs text-slate-500">
          <div>• Check #{shareCheckCount}</div>
          <div className="break-all">• {shareResourceUrl}</div>
          {shareNextCheckIn !== null && (
            <div>
              • {shareNextCheckIn === 0 ? 'checking...' : `next check in ${shareNextCheckIn}s`}
            </div>
          )}
        </div>
      )}
      {sharePreviewStatus === 'ready' && (
        <>
          <p className="text-sm font-semibold text-slate-800">
            Sticker #{stickerId} is ready to share:
          </p>
          {sharePreviewUrl && (
            <div className="mb-4 mt-3">
              <img
                src={sharePreviewUrl}
                alt={`Sticker ${stickerId} share preview`}
                className="w-full rounded-xl border border-slate-200"
              />
            </div>
          )}
          {isMiniApp ? (
            <button
              type="button"
              onClick={onShareFarcaster}
              disabled={shareComposerBusy}
              className="mt-3 w-full rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              Share on Farcaster
            </button>
          ) : (
            <div className="mt-3 flex flex-col gap-2">
              <button
                type="button"
                onClick={onShareX}
                className="w-full rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition"
              >
                Share on X
              </button>
              <button
                type="button"
                onClick={onShareFarcaster}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition"
              >
                Share on Farcaster
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
